import { useState, useMemo, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Trash2,
  ArrowDownToLine,
  CheckCircle,
  CircleAlert,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useStockTransactions,
  useDeleteStockTransaction,
} from "@/hooks/useStockTransactions";
import { useLocation } from "wouter";
import LoadingIndicator from "@/components/LoadingIndicator";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const cleanupBodyLock = () => {
  document.body.style.removeProperty("pointer-events");
  document.body.style.removeProperty("overflow");
};

export const StockInList = () => {
  const { format: formatDecimal } = useDecimalPrecision();
  const { toast } = useToast();
  const [_, navigate] = useLocation();

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);

  // Fetch all data without search filter
  const filters = {
    voucherType: "GI" as const,
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    page: 1,
    limit: 100,
  };

  const { data, isLoading, error } = useStockTransactions(filters);

  // Client-side search across all fields
  const filteredTransactions = useMemo(() => {
    if (!data?.data) return [];

    return data.data.filter((transaction: any) =>
      Object.values(transaction).some((value) =>
        value?.toString().toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [data?.data, search]);

  // Paginate the filtered results
  const paginatedTransactions = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, page, limit]);

  const totalPages = Math.ceil(filteredTransactions.length / limit);
  const deleteTransaction = useDeleteStockTransaction();

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Cleanup body lock on dialog open/close
  useEffect(() => {
    cleanupBodyLock();
    return cleanupBodyLock;
  }, [deleteDialogOpen]);

  const handleDelete = async () => {
    if (!selectedTransactionId) return;

    try {
      await deleteTransaction.mutateAsync(selectedTransactionId, {
        onSuccess: () => {
          toast({
            title: "Transaction Deleted",
            description: "Stock In transaction has been deleted successfully",
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
          });
          setDeleteDialogOpen(false);
          setSelectedTransactionId(null);
        },
        onError: (error: any) => {
          toast({
            title: "Failed to Delete Transaction",
            description: error.response?.data?.message || error.message,
            variant: "destructive",
            icon: <CircleAlert size={30} className="animate-spin-once" />,
          });
        },
      });
    } catch (error) {
      console.error("Error deleting transaction:", error);
    }
  };

  const handleResetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setSearch("");
    setPage(1);
  };

  if (isLoading) return <LoadingIndicator />;

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400 px-4 py-3 rounded">
          Error loading Stock In transactions: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stock In</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage all stock in transactions
          </p>
        </div>
        <Button
          onClick={() => navigate("/stock-in/add")}
          className="bg-green-600 hover:bg-green-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Stock In
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">Filters</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="dateFrom">Date From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="dateTo">Date To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search all fields..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleResetFilters}>
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardContent className="pt-6">
          {paginatedTransactions && paginatedTransactions.length > 0 ? (
            <>
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800">
                  <TableRow>
                    <TableHead>Voucher Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Total Quantity</TableHead>
                    <TableHead className="text-right">
                      Total Cost/Sales (RM)
                    </TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTransactions.map((transaction: any) => (
                    <TableRow key={transaction.voucherNumber}>
                      <TableCell className="font-medium">
                        {transaction.voucherNumber}
                      </TableCell>
                      <TableCell>
                        {format(new Date(transaction.entryDate), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm text-gray-600">
                          {transaction.itemCount}{" "}
                          {transaction.itemCount === 1 ? "item" : "items"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-green-600 font-medium">
                          +{formatDecimal(Number(transaction.totalQuantity || 0))}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatDecimal(Number(transaction.totalAmountIn || 0))}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {transaction.reference || "-"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(`/stock-in/view`, {
                                  state: {
                                    voucherNumber: transaction.voucherNumber,
                                  },
                                })
                              }
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedTransactionId(
                                  transaction.voucherNumber
                                );
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {filteredTransactions.length > limit && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-600">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, filteredTransactions.length)} of{" "}
                    {filteredTransactions.length} vouchers
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        Page {page} of {totalPages}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <div className="mb-4">
                <ArrowDownToLine className="h-12 w-12 mx-auto text-green-300" />
              </div>
              <p className="text-lg font-medium">
                No Stock In transactions found
              </p>
              <p className="text-sm mt-2">
                {search || dateFrom || dateTo
                  ? "Try adjusting your filters"
                  : "Create your first Stock In transaction to get started"}
              </p>
              {!search && !dateFrom && !dateTo && (
                <Button
                  onClick={() => navigate("/stock-in/add")}
                  className="bg-green-600 hover:bg-green-700 mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stock In
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will delete the Stock In voucher and all its items,
              reversing effects on inventory quantities. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedTransactionId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteTransaction.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
